import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DateTime } from 'luxon';
import { lastValueFrom, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { SyncState } from 'src/app/pages/payroll/store/model';
import { environment } from 'src/environments/environment';
import { HttpRequestOptions } from '../api.service';

export interface IntegrationsApiResponse<T> {
  data?: T;
  message: string;
  statusCode: number;
}

export interface Integration {
  appName: string;
  displayName: string;
  connectionMethod: string;
  oauthUrl: string;
  connected: boolean;
}

export interface IntegrationToken {
  appName: string;
  companyId: string;
  deploymentId: string;
  accessToken: string;
  refreshToken: string;
  expiryAccessToken: DateTime;
  expiryRefreshToken: DateTime;
  authorizationCode: string;
}




@Injectable({ providedIn: 'root' })
export class IntegrationsService {
  private readonly integrationsApiUrl = environment.integrationsApiUrl;

  constructor(
    private httpClient: HttpClient,
  ) { }

  public getIntegrations(options: HttpRequestOptions = {}) {
    return lastValueFrom(this.httpClient.get<IntegrationsApiResponse<Integration[]>>(`${this.integrationsApiUrl}/v1/integrations`, options)
      .pipe(map(response => response.data)));
  }

  public getToken(options: HttpRequestOptions = {}): Observable<IntegrationToken> {
    return this.httpClient.get<IntegrationsApiResponse<IntegrationToken>>(`${this.integrationsApiUrl}/v1/access-token`, options)
      .pipe(map(response => response.data));
  }

  public deleteToken(options: HttpRequestOptions = {}): Observable<string> {
    return this.httpClient.delete<IntegrationsApiResponse<string>>(`${this.integrationsApiUrl}/v1/delete-token`, options)
      .pipe(map(response => response.data));
  }

  public postToken(body, options: HttpRequestOptions = {}): Observable<IntegrationsApiResponse<string>> {
    return this.httpClient.post<IntegrationsApiResponse<string>>(`${this.integrationsApiUrl}/v1/token`, body, options);
  }

  public syncDataToDeel(body, options: HttpRequestOptions = {}): Observable<IntegrationsApiResponse<boolean>> {
    return this.httpClient.post<IntegrationsApiResponse<any>>(`${this.integrationsApiUrl}/v1/deel/sync/users`, body, options)
      .pipe(map(response => response.data));
  }

  public getDeelSyncStatus(options: HttpRequestOptions = {}): Observable<SyncState> {
    return this.httpClient.get<IntegrationsApiResponse<SyncState>>(`${this.integrationsApiUrl}/v1/deel/sync/last-batch`, options)
      .pipe(map(response => response.data));
  }

  public getMarketplaceIntegrations(options: HttpRequestOptions = {}): Observable<MarketplaceIntegration[]> {
    return this.httpClient.get<MarketplaceApiResponse>(`${this.integrationsApiUrl}/v1/marketplace/integrations`, options)
      .pipe(
        map(response => {
          if (!response?.data?.integrationList) {
            return [];
          }
          return response.data.integrationList.map(item => this.mapApiIntegrationToMarketplaceIntegration(item));
        }),
      );
  }

  public getMarketplaceData(
    options: HttpRequestOptions = {},
  ): Observable<{
    integrations: MarketplaceIntegration[];
    categoryConfig: Record<string, any>;
  }> {
    return this.httpClient.get<MarketplaceApiResponse>(`${this.integrationsApiUrl}/v1/marketplace/integrations`, options)
      .pipe(
        map(response => {
          const integrations = response?.data?.integrationList
            ? response.data.integrationList.map(item => this.mapApiIntegrationToMarketplaceIntegration(item))
            : [];
          const categoryConfig = response?.data?.categoryConfig || {};
          return { integrations, categoryConfig };
        }),
      );
  }

  private mapApiIntegrationToMarketplaceIntegration(apiItem: MarketplaceApiIntegration): MarketplaceIntegration {
    return {
      id: apiItem.id,
      key: apiItem.key,
      name: apiItem.name,
      description: apiItem.description ?? '',
      logoUrl: apiItem.logoUri,
      price: undefined, // Will be populated from plans API
      billingCycle: undefined, // Will be populated from plans API
      buyNowUrl: undefined, // Not provided in API response
      disabled: apiItem.isDeactivated || false,
      category: apiItem.internalFields?.category,
      tags: [],
      // Store original API data for reference
      originalData: apiItem,
    };
  }

  /**
   * Create a connection for a marketplace integration
   * Based on API documentation: POST /v1/marketplace/connections
   * @param integrationKey - The integration key (required)
   * @param authOptionKey - Optional auth option key (uses default if not specified)
   * @param connectionParameters - Connection credentials matching the auth schema
   * @param redirectUri - OAuth redirect URI (optional)
   * @param options - HTTP request options
   */
  public createMarketplaceConnection(
    integrationKey: string,
    authOptionKey: string | null,
    connectionParameters: Record<string, any>,
    redirectUri?: string,
    options: HttpRequestOptions = {},
  ): Observable<IntegrationsApiResponse<{ authUrl?: string, connectionId?: string, success: boolean, message?: string }>> {
    const body: {
      integrationId: string;
      authOptionKey?: string;
      connectionParameters: Record<string, any>;
      redirectUri?: string;
    } = {
      integrationId: integrationKey,
      connectionParameters,
    };

    // Only include authOptionKey if provided
    if (authOptionKey) {
      body.authOptionKey = authOptionKey;
    }

    // Only include redirectUri if provided
    if (redirectUri) {
      body.redirectUri = redirectUri;
    }

    return this.httpClient.post<IntegrationsApiResponse<{ authUrl?: string, connectionId?: string, success: boolean, message?: string }>>(
      `${this.integrationsApiUrl}/v1/marketplace/connections`,
      body,
      options,
    );
  }

  /**
   * Get configuration flows for a marketplace integration
   * Based on API documentation: GET /v1/marketplace/flows
   * @param integrationKey - The integration key (required)
   * @param connectionId - Optional connection ID (query param when integration is connected)
   * @param options - HTTP request options
   */
  /**
   * GET /v1/marketplace/flows returns data as array directly: { data: Flow[], message, statusCode }
   */
  public getMarketplaceFlows(
    integrationKey: string,
    connectionId?: string | null,
    options: HttpRequestOptions = {},
  ): Observable<IntegrationsApiResponse<any[]>> {
    const params: Record<string, string> = {
      ...(options.params || {}),
      integrationKey,
    };
    if (connectionId) {
      params.connectionId = connectionId;
    }
    const requestOptions: HttpRequestOptions = {
      ...options,
      params,
    };
    return this.httpClient.get<IntegrationsApiResponse<any[]>>(
      `${this.integrationsApiUrl}/v1/marketplace/flows`,
      requestOptions,
    );
  }

  /**
   * Create flows for a marketplace connection
   * Based on API documentation: POST /v1/marketplace/flows
   * @param connectionId - The connection ID (required)
   * @param options - HTTP request options
   */
  public createMarketplaceFlows(
    connectionId: string,
    options: HttpRequestOptions = {},
  ): Observable<IntegrationsApiResponse<unknown>> {
    return this.httpClient.post<IntegrationsApiResponse<unknown>>(
      `${this.integrationsApiUrl}/v1/marketplace/flows`,
      { connectionId },
      options,
    );
  }

  /**
   * Reconnect an errored/disconnected marketplace connection
   * Based on API documentation: PUT /v1/marketplace/connections/{connectionId} (MarketplaceController_reconnectConnection)
   * @param connectionId - The connection ID (required)
   * @param authOptionKey - Optional auth option key for the auth method to use
   * @param connectionParameters - Optional connection credentials (for client-credentials auth)
   * @param redirectUri - OAuth redirect URI (for OAuth flows)
   * @param options - HTTP request options
   */
  public reconnectMarketplaceConnection(
    connectionId: string,
    authOptionKey?: string | null,
    connectionParameters?: Record<string, any>,
    redirectUri?: string,
    options: HttpRequestOptions = {},
  ): Observable<IntegrationsApiResponse<{ authUrl?: string, connectionId?: string, success?: boolean, message?: string }>> {
    const body: Record<string, any> = {};
    if (authOptionKey) {
      body.authOptionKey = authOptionKey;
    }
    if (connectionParameters && Object.keys(connectionParameters).length > 0) {
      body.connectionParameters = connectionParameters;
    }
    if (redirectUri) {
      body.redirectUri = redirectUri;
    }

    return this.httpClient.put<IntegrationsApiResponse<{ authUrl?: string, connectionId?: string, success?: boolean, message?: string }>>(
      `${this.integrationsApiUrl}/v1/marketplace/connections/${connectionId}`,
      Object.keys(body).length > 0 ? body : {},
      options,
    );
  }

  /**
   * Delete (disconnect) a marketplace connection
   * Based on API documentation: DELETE /v1/marketplace/connections/{connectionId}
   * Similar to PUT reconnect but uses DELETE method.
   * @param connectionId - The connection ID (required)
   * @param options - HTTP request options
   */
  public deleteMarketplaceConnection(
    connectionId: string,
    options: HttpRequestOptions = {},
  ): Observable<IntegrationsApiResponse<{ success?: boolean, message?: string }>> {
    return this.httpClient.delete<IntegrationsApiResponse<{ success?: boolean, message?: string }>>(
      `${this.integrationsApiUrl}/v1/marketplace/connections/${connectionId}`,
      options,
    );
  }

  /**
   * Update a marketplace connection configuration
   * Based on API documentation: PUT /v1/marketplace/connections/{connectionId}
   * @param connectionId - The connection ID (required)
   * @param authOptionKey - Optional auth option key
   * @param connectionParameters - Updated connection credentials matching the auth schema
   * @param options - HTTP request options
   */
  public updateMarketplaceConnection(
    connectionId: string,
    authOptionKey: string | null,
    connectionParameters: Record<string, any>,
    options: HttpRequestOptions = {},
  ): Observable<IntegrationsApiResponse<{ success: boolean, message?: string }>> {
    const body: {
      authOptionKey?: string;
      connectionParameters: Record<string, any>;
    } = {
      connectionParameters,
    };

    // Only include authOptionKey if provided
    if (authOptionKey) {
      body.authOptionKey = authOptionKey;
    }

    return this.httpClient.put<IntegrationsApiResponse<{ success: boolean, message?: string }>>(
      `${this.integrationsApiUrl}/v1/marketplace/connections/${connectionId}`,
      body,
      options,
    );
  }

  /**
   * Patch flow parameters for a given flow instance
   * Based on API documentation: PATCH /v1/marketplace/flows/{flowId}
   * @param flowId - The flow instance ID to patch (required)
   * @param parameters - Flow parameters to update, matching the flow's parametersSchema
   * @param options - HTTP request options (tdCompanyId, deploymentId added by HttpBillingInterceptor)
   */
  public patchFlow(
    flowId: string,
    parameters: Record<string, unknown>,
    options: HttpRequestOptions = {},
  ): Observable<IntegrationsApiResponse<{ message?: string }>> {
    const body = {
      parameters: JSON.stringify(parameters),
    };
    return this.httpClient.patch<IntegrationsApiResponse<{ message?: string }>>(
      `${this.integrationsApiUrl}/v1/marketplace/flows/${flowId}`,
      body,
      options,
    );
  }

  public patchFlowEnabled(
    flowId: string,
    enabled: boolean,
    options: HttpRequestOptions = {},
  ): Observable<IntegrationsApiResponse<{ message?: string }>> {
    const body = { enabled };
    return this.httpClient.patch<IntegrationsApiResponse<{ message?: string }>>(
      `${this.integrationsApiUrl}/v1/marketplace/flows/${flowId}`,
      body,
      options,
    );
  }

  /**
   * Create a flow run for a given flow instance.
   * Based on API documentation: POST /v1/marketplace/flows/{flowId}/flow-runs?node=...
   *
   * `input` is the data passed to the api-trigger node for this run — used by
   * the auto-render branch where the form represents the api-trigger node's
   * `inputSchema` (no flow-level parametersSchema). The body field is `input`
   * (JSON-encoded string) per the marketplace API contract.
   *
   * Pass `undefined` (the default) for the SYNC NOW path on a configured flow,
   * where the flow's parameters were already persisted via `patchFlow` and the
   * trigger doesn't need additional input.
   */
  public createFlowRun(
    flowId: string,
    node: string,
    input?: Record<string, unknown>,
    options: HttpRequestOptions = {},
  ): Observable<IntegrationsApiResponse<unknown>> {
    const requestOptions: HttpRequestOptions = {
      ...options,
      params: {
        ...(options.params || {}),
        node,
      },
    };
    const body = input === undefined ? null : { input: JSON.stringify(input) };
    return this.httpClient.post<IntegrationsApiResponse<unknown>>(
      `${this.integrationsApiUrl}/v1/marketplace/flows/${flowId}/flow-runs`,
      body,
      requestOptions,
    );
  }

  /**
   * Get reference collection list for Configure Flow
   * POST v1/membrane/proxy/connections/{connectionId}/data/{referenceCollectionKey}/list
   * Uses cursor-based pagination. tdCompanyId and deploymentId are added by HttpBillingInterceptor.
   * @param connectionId - The connection ID (required)
   * @param referenceCollectionKey - The reference collection key from parametersSchema (required)
   * @param body - Request body: { cursor?, parameters?, filter?, unifiedFilter? }
   * @param options - HTTP request options
   */
  public getReferenceCollectionList(
    connectionId: string,
    referenceCollectionKey: string,
    body: ReferenceCollectionListRequestBody = {},
    options: HttpRequestOptions = {},
  ): Observable<NormalizedReferenceCollectionList> {
    return this.httpClient
      .post<ReferenceCollectionListResponse>(
        `${this.integrationsApiUrl}/v1/membrane/proxy/connections/${connectionId}/data/${referenceCollectionKey}/list`,
        body,
        options,
      )
      .pipe(map((res) => this.normalizeReferenceCollectionResponse(res)));
  }

  /**
   * Get flow run history for a marketplace connection.
   * GET /v1/marketplace/connections/{connectionId}/flow-runs?limit=40&cursor=0
   * tdCompanyId and deploymentId query params are added by HttpBillingInterceptor.
   */
  public getConnectionFlowRuns(
    connectionId: string,
    query: FlowRunHistoryListQuery = {},
    options: HttpRequestOptions = {},
  ): Observable<NormalizedFlowRunHistoryList> {
    const params: Record<string, string> = {
      ...(options.params || {}),
      limit: String(query.limit ?? 40),
      cursor: String(query.cursor ?? '0'),
    };

    return this.httpClient
      .get<any>(
        `${this.integrationsApiUrl}/v1/marketplace/connections/${connectionId}/flow-runs`,
        {
          ...options,
          params,
        },
      )
      .pipe(map((res) => this.normalizeFlowRunHistoryResponse(res)));
  }

  private normalizeReferenceCollectionResponse(res: ReferenceCollectionListResponse): NormalizedReferenceCollectionList {
    const records = res.records ?? [];
    const cursor = res.cursor ?? undefined;
    return {
      items: records,
      cursor,
      hasMore: !!cursor,
    };
  }

  private normalizeFlowRunHistoryResponse(res: any): NormalizedFlowRunHistoryList {
    const data = res?.data;
    const items = Array.isArray(data?.items) ? data.items : [];
    const cursorValue = data?.cursor ?? res?.cursor;
    const cursor = cursorValue !== undefined && cursorValue !== null && cursorValue !== ''
      ? String(cursorValue)
      : undefined;

    return {
      items,
      cursor,
      hasMore: !!cursor,
    };
  }

}

/** Request body for Membrane proxy data collection list */
export interface ReferenceCollectionListRequestBody {
  parameters?: Record<string, unknown>;
  filter?: Record<string, unknown>;
  unifiedFilter?: Record<string, unknown[]>;
  cursor?: string;
  limit?: number;
  /**
   * Free-text query for the integration backend to filter records by. The
   * membrane proxy forwards it to the third-party API; backends that don't
   * support search ignore it. The UI only surfaces a search input when the
   * Control opts in via `options.searchable: true`.
   */
  search?: string;
}

/** API response shape (records + cursor for next page) */
export interface ReferenceCollectionListResponse {
  records?: any[];
  cursor?: string;
}

/** Normalized list for UI consumption */
export interface NormalizedReferenceCollectionList {
  items: any[];
  cursor?: string;
  hasMore: boolean;
}

export interface FlowRunHistoryListQuery {
  limit?: number;
  cursor?: string;
}

export interface NormalizedFlowRunHistoryList {
  items: any[];
  cursor?: string;
  hasMore: boolean;
}

export interface MarketplaceApiResponse {
  data: {
    categoryConfig?: Record<string, any>;
    integrationList: MarketplaceApiIntegration[];
  };
  message: string;
  statusCode: number;
}

export interface MarketplaceApiIntegration {
  id: string;
  name: string;
  description: string;
  uuid: string;
  key: string;
  state: string;
  errors: any[];
  revision: string;
  createdAt: string;
  updatedAt: string;
  isDeactivated: boolean;
  logoUri?: string;
  connectorId: string;
  connectorVersion?: string;
  hasMissingParameters: boolean;
  hasDocumentation: boolean;
  hasOperations: boolean;
  operationsCount: number;
  hasData: boolean;
  dataCollectionsCount: number;
  hasEvents: boolean;
  eventsCount: number;
  hasGlobalWebhooks: boolean;
  hasUdm: boolean;
  authType: string;
  authOptions: any[];
  connection?: any; // Connection property from API response
  internalFields?: {
    description: string;
    category: string;
    allowConfigure: boolean;
    isEligible: boolean;
  };
}

export interface MarketplaceIntegration {
  id: string;
  key: string; // Integration key (e.g., "bamboohr", "zoho-people") used to match with planId
  name: string;
  description: string;
  logoUrl?: string;
  logoIcon?: string;
  price?: string;
  billingCycle?: string;
  buyNowUrl?: string;
  disabled?: boolean;
  category?: string;
  tags?: string[];
  originalData?: MarketplaceApiIntegration; // Store original API response for reference
}
